// src/firebase.ts
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  User,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  writeBatch,
  serverTimestamp
} from "firebase/firestore";
import Papa from 'papaparse';
import { validateCSVRow } from './utils/validation';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize App Check for bot protection (production only)
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true
  });
}

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);

// User types
export type UserRole = 'admin' | 'user';

export interface UserProfile {
  uid: string;
  email: string;
  role: UserRole;
  createdAt: any;
  displayName?: string;
  emailVerified?: boolean;
  disabled?: boolean;
  disabledAt?: any;
  disabledBy?: string;
  originalRole?: UserRole;
  createdBy?: string;
  lastLogin?: any;
}

export interface FeeSchedule {
  id: string;
  name: string;
  data: any[];
  uploadedBy: string;
  uploadedAt: Date;
  isActive: boolean;
}

export interface CalculationHistory {
  id: string;
  userId: string;
  userEmail: string;
  calculationData: any;
  results: any;
  timestamp: Date;
}

// UPDATED: CPT Data interface with Multiple Procedure Indicator and Bilateral Procedure Indicator fields
export interface CPTData {
  cptCode: string;
  charge: number;
  description?: string;
  
  // Multiple Procedure Indicator fields (support different naming conventions)
  MultipleProcedureIndicator?: string;
  multipleProcedureIndicator?: string;
  'Multiple Procedure Indicator'?: string;
  
  // Endoscopy base code fields (support different naming conventions)
  EndoBase?: string;
  endoBase?: string;
  'Endo Base'?: string;
  
  // NEW: Bilateral Procedure Indicator fields (support different naming conventions)
  BilateralProcedureIndicator?: string;
  bilateralProcedureIndicator?: string;
  'Bilateral Procedure Indicator'?: string;
  
  // Insurance carrier columns (dynamic)
  [key: string]: any;
}

// NEW: Cache for base procedure data to avoid repeated lookups
let baseProcedureCache: Map<string, number> = new Map();

// NEW: Function to get base procedure allowable amount
export const getBaseProcedureAllowable = async (baseCode: string, insuranceCarrier: string): Promise<number> => {
  const cacheKey = `${baseCode}_${insuranceCarrier}`;
  
  // Check cache first
  if (baseProcedureCache.has(cacheKey)) {
    return baseProcedureCache.get(cacheKey)!;
  }

  try {
    const baseProcData = await getCPTData([baseCode]);
    if (baseProcData.length === 0) {
      console.warn(`Base procedure ${baseCode} not found in fee schedule`);
      return 0;
    }

    const baseProcAllowable = (baseProcData[0] as any)[insuranceCarrier];
    if (!baseProcAllowable) {
      console.warn(`Base procedure ${baseCode} has no allowable for ${insuranceCarrier}`);
      return 0;
    }

    const allowableAmount = Number(baseProcAllowable);
    
    // Cache the result
    baseProcedureCache.set(cacheKey, allowableAmount);
    
    return allowableAmount;
  } catch (error) {
    console.error('Error getting base procedure allowable:', error);
    return 0;
  }
};

// NEW: Function to clear the base procedure cache (useful when fee schedule changes)
export const clearBaseProcedureCache = (): void => {
  baseProcedureCache.clear();
};

// CSV parsing function - FIXED to accept string input
export const parseCSVFile = async (csvText: string): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      delimitersToGuess: [',', '\t', '|', ';'],
      complete: (results) => {
        if (results.errors.length > 0) {
          console.warn('CSV parsing warnings:', results.errors);
        }
        // Clean up headers by trimming whitespace
        const cleanData = results.data.map((row: any) => {
          const cleanRow: any = {};
          Object.keys(row).forEach(key => {
            const cleanKey = key.trim();
            cleanRow[cleanKey] = row[key];
          });
          return cleanRow;
        });
        resolve(cleanData);
      },
      error: (error: any) => reject(error)
    });
  });
};

// Email verification functions
export const sendVerificationEmail = async (user: User): Promise<void> => {
  try {
    await sendEmailVerification(user);
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};

export const checkEmailVerified = (user: User | null): boolean => {
  return user?.emailVerified || false;
};

// Auth state observer
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Rate limiting for authentication operations
const rateLimitMap = new Map<string, { attempts: number, lastAttempt: number, blockUntil?: number }>();

const checkRateLimit = (identifier: string, maxAttempts: number = 5, windowMs: number = 900000): void => {
  const now = Date.now();
  const record = rateLimitMap.get(identifier);

  // Check if currently blocked
  if (record?.blockUntil && now < record.blockUntil) {
    const remainingMs = record.blockUntil - now;
    const remainingMinutes = Math.ceil(remainingMs / 60000);
    throw new Error(`Too many attempts. Please try again in ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}.`);
  }

  // Reset if outside window
  if (!record || now - record.lastAttempt > windowMs) {
    rateLimitMap.set(identifier, { attempts: 1, lastAttempt: now });
    return;
  }

  // Check if exceeded limit
  if (record.attempts >= maxAttempts) {
    const blockUntil = now + windowMs;
    rateLimitMap.set(identifier, { ...record, blockUntil });
    throw new Error(`Too many attempts. Please try again in 15 minutes.`);
  }

  // Increment attempts
  record.attempts++;
  record.lastAttempt = now;
};

const resetRateLimit = (identifier: string): void => {
  rateLimitMap.delete(identifier);
};

// Authentication functions
export const registerUser = async (email: string, password: string, role: UserRole = 'user'): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Send verification email immediately after registration
    await sendVerificationEmail(user);
    
    // Create user profile in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      role: role,
      createdAt: serverTimestamp(),
      displayName: user.displayName || email.split('@')[0],
      emailVerified: false
    });
    
    return user;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

export const loginUser = async (email: string, password: string): Promise<User> => {
  // SECURITY: Rate limiting to prevent brute force attacks
  checkRateLimit(`login:${email.toLowerCase()}`, 5, 900000); // 5 attempts per 15 minutes

  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Reset rate limit on successful login
    resetRateLimit(`login:${email.toLowerCase()}`);
    
    // Check if email is verified
    if (!user.emailVerified) {
      const verificationError = new Error('Please verify your email address before logging in.');
      (verificationError as any).needsVerification = true;
      (verificationError as any).user = user;
      throw verificationError;
    }
    
    // Get user profile to check if disabled
    const userProfile = await getUserProfile(user.uid);
    
    // Check if user is disabled
    if (userProfile?.disabled) {
      // Sign out the disabled user immediately
      await signOut(auth);
      throw new Error('Your account has been disabled. Please contact an administrator for assistance.');
    }
    
    // Check if user profile exists
    if (!userProfile) {
      // Check if this is a pending user (created by admin, first login)
      const pendingUserDoc = await getDoc(doc(db, 'pendingUsers', user.email!.toLowerCase()));

      if (pendingUserDoc.exists()) {
        // Migrate pending user to regular users collection
        const pendingData = pendingUserDoc.data();
        const newUserProfile: UserProfile = {
          uid: user.uid,
          email: user.email!.toLowerCase(),
          displayName: pendingData.displayName || '',
          role: pendingData.role || 'user',
          createdAt: pendingData.createdAt,
          emailVerified: true,
          createdBy: pendingData.createdBy,
          lastLogin: serverTimestamp()
        };

        // Create real user profile
        await setDoc(doc(db, 'users', user.uid), newUserProfile);

        // Delete pending user record
        await deleteDoc(doc(db, 'pendingUsers', user.email!.toLowerCase()));

        // Login successful - profile now exists
        return user;
      }

      await signOut(auth);
      throw new Error('User profile not found. Please contact an administrator.');
    }

    // Update user document to reflect verification status and last login
    await updateDoc(doc(db, 'users', user.uid), {
      emailVerified: true,
      lastLogin: serverTimestamp()
    });
    
    return user;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

// User profile functions
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        uid: data.uid,
        email: data.email,
        role: data.role,
        createdAt: data.createdAt,
        displayName: data.displayName,
        emailVerified: data.emailVerified,
        disabled: data.disabled,
        disabledAt: data.disabledAt,
        disabledBy: data.disabledBy,
        originalRole: data.originalRole,
        createdBy: data.createdBy
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid: string, updates: Partial<UserProfile>): Promise<void> => {
  try {
    const docRef = doc(db, 'users', uid);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Fast CPT data query function
export const getCPTData = async (cptCodes: string[]): Promise<CPTData[]> => {
  try {
    const promises = cptCodes.map(cpt => 
      getDoc(doc(db, 'cptData', cpt.trim()))
    );
    
    const docs = await Promise.all(promises);
    return docs
      .filter(doc => doc.exists())
      .map(doc => ({ cptCode: doc.id, ...doc.data() } as CPTData));
  } catch (error) {
    console.error('Error getting CPT data:', error);
    throw error;
  }
};

// Fee schedule upload with individual CPT document storage - FIXED function signature
export const uploadFeeSchedule = async (name: string, data: any[], uploadedBy: string): Promise<string> => {
  try {
    console.log('Starting fee schedule upload:', name, data.length, 'records');
    
    // Safeguard against extremely large files
    if (data.length > 50000) {
      throw new Error(`File too large: ${data.length} records. Maximum supported: 50,000 records.`);
    }

    // Create fee schedule metadata document first
    const scheduleDocRef = await addDoc(collection(db, 'feeSchedules'), {
      name: name,
      uploadedBy: uploadedBy,
      uploadedAt: serverTimestamp(),
      isActive: true,
      recordCount: data.length,
      processingStatus: 'processing'
    });
    
    console.log('Fee schedule metadata created with ID:', scheduleDocRef.id);

    // Process data in batches of 200 (Firestore batch limit is 500, but we'll use 200 for safety)
    const batchSize = 200;
    const batches = [];
    const validRecords: string[] = [];
    const errorRecords: string[] = [];
    
    for (let i = 0; i < data.length; i += batchSize) {
      const batch = writeBatch(db);
      let batchCount = 0;
      const batchEnd = Math.min(i + batchSize, data.length);
      
      const chunk = data.slice(i, batchEnd);
      
      chunk.forEach((row, index) => {
        try {
          // Validate and sanitize the row
          const validation = validateCSVRow(row, i + index + 1);

          if (!validation.isValid) {
            errorRecords.push(...validation.errors);
            return;
          }

          const sanitizedData = validation.sanitizedData!;

          // Extract modifier if present (check various capitalizations)
          const modifierValue = sanitizedData['Modifier'] || sanitizedData['modifier'] || sanitizedData['MODIFIER'] || '';
          const modifier = typeof modifierValue === 'string' ? modifierValue.trim() : '';

          // Create document ID: {cptCode}__{modifier} or just {cptCode} if no modifier
          const docId = modifier ? `${sanitizedData.cptCode}__${modifier}` : sanitizedData.cptCode;

          // Create clean document with sanitized data
          const cleanRow: any = {
            cptCode: sanitizedData.cptCode,
            modifier: modifier || null, // Store modifier field explicitly
            charge: sanitizedData.charge,
            description: sanitizedData.description,
            uploadedAt: serverTimestamp(),
            scheduleId: scheduleDocRef.id
          };

          // Add all other sanitized columns (like insurance carriers, indicators, etc.)
          Object.keys(sanitizedData).forEach(key => {
            if (!['cptCode', 'charge', 'description', 'Modifier', 'modifier', 'MODIFIER'].includes(key)) {
              cleanRow[key] = sanitizedData[key];
            }
          });

          const docRef = doc(db, 'cptData', docId);
          batch.set(docRef, cleanRow);
          batchCount++;
          validRecords.push(docId);

        } catch (rowError) {
          console.error('Error processing row:', i + index + 1, rowError);
          errorRecords.push(`Row ${i + index + 1}: ${rowError}`);
        }
      });
      
      if (batchCount > 0) {
        batches.push(batch);
      }
    }
    
    // Execute all batches
    console.log('Executing', batches.length, 'batches...');
    for (let i = 0; i < batches.length; i++) {
      await batches[i].commit();
      console.log(`Batch ${i + 1}/${batches.length} committed`);
      
      // Add small delay between batches to avoid overwhelming Firestore
      if (i < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    // Update the schedule metadata with final status
    const updateData: any = { 
      processingStatus: 'completed',
      validRecords: validRecords.length,
      errorRecords: errorRecords.length,
      completedAt: serverTimestamp()
    };
    
    // Only store first 50 errors to avoid Firestore limits
    if (errorRecords.length > 0) {
      const maxErrors = 50;
      const limitedErrors = errorRecords.slice(0, maxErrors);
      if (errorRecords.length > maxErrors) {
        limitedErrors.push(`... and ${errorRecords.length - maxErrors} more errors`);
      }
      updateData.sampleErrors = limitedErrors;
    }
    
    await updateDoc(doc(db, 'feeSchedules', scheduleDocRef.id), updateData);
    
    console.log('Fee schedule upload completed:');
    console.log('- Valid records:', validRecords.length);
    console.log('- Error records:', errorRecords.length);
    
    if (errorRecords.length > 0) {
      console.warn('Upload completed with errors:', errorRecords.slice(0, 10));
      if (errorRecords.length > 50) {
        console.warn(`Total of ${errorRecords.length} errors found. First 50 stored in fee schedule metadata.`);
      }
    }
    
    // Clear base procedure cache when new fee schedule is uploaded
    clearBaseProcedureCache();
    
    return scheduleDocRef.id;
    
  } catch (error) {
    console.error('Error uploading fee schedule:', error);
    throw error;
  }
};

// FIXED: uploadFeeScheduleWithCacheReset function
export const uploadFeeScheduleWithCacheReset = async (name: string, data: any[], uploadedBy: string): Promise<FeeSchedule> => {
  try {
    const scheduleId = await uploadFeeSchedule(name, data, uploadedBy);
    
    // Get the uploaded schedule
    const scheduleDoc = await getDoc(doc(db, 'feeSchedules', scheduleId));
    if (!scheduleDoc.exists()) {
      throw new Error('Failed to retrieve uploaded fee schedule');
    }
    
    const scheduleData = scheduleDoc.data();
    return {
      id: scheduleId,
      name: scheduleData.name,
      data: [], // Don't return the full data array
      uploadedBy: scheduleData.uploadedBy,
      uploadedAt: scheduleData.uploadedAt.toDate(),
      isActive: scheduleData.isActive
    };
  } catch (error) {
    console.error('Error in uploadFeeScheduleWithCacheReset:', error);
    throw error;
  }
};

export const getActiveFeeSchedule = async (): Promise<FeeSchedule | null> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'feeSchedules'));
    const schedules = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        data: data.data,
        uploadedBy: data.uploadedBy,
        uploadedAt: data.uploadedAt.toDate(),
        isActive: data.isActive
      };
    });

    const activeSchedule = schedules.find(schedule => schedule.isActive);
    return activeSchedule || null;
  } catch (error) {
    console.error('Error getting active fee schedule:', error);
    return null;
  }
};

export const getCPTDataInfo = async (): Promise<{ available: boolean; count: number }> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'cptData'));
    return { available: querySnapshot.docs.length > 0, count: querySnapshot.docs.length };
  } catch (error) {
    console.error('Error getting CPT data info:', error);
    return { available: false, count: 0 };
  }
};

// Get available modifiers for a specific CPT code
export const getModifiersForCPT = async (cptCode: string): Promise<string[]> => {
  try {
    const cptCodeTrimmed = cptCode.trim();

    // Query for all documents that start with this CPT code
    const querySnapshot = await getDocs(collection(db, 'cptData'));
    const modifiers: string[] = [];

    querySnapshot.forEach(doc => {
      const docId = doc.id;
      const data = doc.data();

      // Check if this document belongs to the CPT code
      if (docId === cptCodeTrimmed) {
        // Base CPT without modifier
        modifiers.push('');
      } else if (docId.startsWith(cptCodeTrimmed + '__')) {
        // CPT with modifier (format: cptCode__modifier)
        const modifier = data.modifier || docId.split('__')[1] || '';
        if (modifier) {
          modifiers.push(modifier);
        }
      }
    });

    return modifiers;
  } catch (error) {
    console.error('Error getting modifiers for CPT:', error);
    return [];
  }
};

// Get CPT data with optional modifier
export const getCPTDataWithModifier = async (cptCode: string, modifier?: string): Promise<CPTData | null> => {
  try {
    const cptCodeTrimmed = cptCode.trim();
    const docId = modifier ? `${cptCodeTrimmed}__${modifier}` : cptCodeTrimmed;

    const docSnap = await getDoc(doc(db, 'cptData', docId));

    if (docSnap.exists()) {
      return { cptCode: docSnap.id, ...docSnap.data() } as CPTData;
    }

    return null;
  } catch (error) {
    console.error('Error getting CPT data with modifier:', error);
    return null;
  }
};

export const getAllFeeSchedules = async (): Promise<FeeSchedule[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'feeSchedules'));
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        data: data.data,
        uploadedBy: data.uploadedBy,
        uploadedAt: data.uploadedAt.toDate(),
        isActive: data.isActive
      };
    });
  } catch (error) {
    console.error('Error getting fee schedules:', error);
    throw error;
  }
};

export const deleteFeeSchedule = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'feeSchedules', id));
  } catch (error) {
    console.error('Error deleting fee schedule:', error);
    throw error;
  }
};

// Helper function to remove undefined values from objects
const cleanObject = (obj: any): any => {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(cleanObject);

  const cleaned: any = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleaned[key] = cleanObject(obj[key]);
    }
  }
  return cleaned;
};

// Calculation history functions
export const saveCalculation = async (userId: string, userEmail: string, calculationData: any, results: any): Promise<string> => {
  try {
    console.log('🔥 Firebase saveCalculation called with userId:', userId, 'email:', userEmail);
    console.log('🔥 Original calculationData:', calculationData);
    console.log('🔥 Original results:', results);

    // Remove undefined values to prevent Firestore errors
    const cleanedCalculationData = cleanObject(calculationData);
    const cleanedResults = cleanObject(results);

    console.log('🔥 Cleaned calculationData:', cleanedCalculationData);
    console.log('🔥 Cleaned results:', cleanedResults);

    const docRef = await addDoc(collection(db, 'calculations'), {
      userId,
      userEmail,
      calculationData: cleanedCalculationData,
      results: cleanedResults,
      timestamp: serverTimestamp()
    });
    console.log('🔥 Firestore document created with ID:', docRef.id);
    return docRef.id;
  } catch (error: any) {
    console.error('🔥 Firebase saveCalculation error:', error);
    console.error('🔥 Error code:', error.code);
    console.error('🔥 Error message:', error.message);
    throw error;
  }
};

export const getUserCalculations = async (userId: string): Promise<CalculationHistory[]> => {
  try {
    // SECURITY FIX: Server-side filtering to prevent IDOR vulnerability
    // Only fetch calculations for the specific user, not all calculations
    const q = query(
      collection(db, 'calculations'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc')
    );
    const querySnapshot = await getDocs(q);

    const userCalculations = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        userEmail: data.userEmail,
        calculationData: data.calculationData,
        results: data.results,
        timestamp: data.timestamp.toDate()
      };
    });

    return userCalculations;
  } catch (error) {
    console.error('Error getting user calculations:', error);
    throw error;
  }
};

export const getAllCalculations = async (): Promise<CalculationHistory[]> => {
  try {
    // SECURITY: Added limit to prevent exposing all calculation data at once
    // TODO: Implement proper pagination in UI
    const q = query(
      collection(db, 'calculations'),
      orderBy('timestamp', 'desc'),
      limit(100) // Limit to 100 calculations per query
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        userEmail: data.userEmail,
        calculationData: data.calculationData,
        results: data.results,
        timestamp: data.timestamp.toDate()
      };
    });
  } catch (error) {
    console.error('Error getting all calculations:', error);
    throw error;
  }
};

// Paginated version for large datasets
export const getAllCalculationsPaginated = async (
  pageSize: number = 50,
  lastDoc?: DocumentSnapshot
): Promise<{ calculations: CalculationHistory[], lastDoc: DocumentSnapshot | null }> => {
  try {
    let q = query(
      collection(db, 'calculations'),
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    const calculations = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        userEmail: data.userEmail,
        calculationData: data.calculationData,
        results: data.results,
        timestamp: data.timestamp.toDate()
      };
    });

    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    return { calculations, lastDoc: lastVisible };
  } catch (error) {
    console.error('Error getting paginated calculations:', error);
    throw error;
  }
};

// User management functions (admin only)
export const getAllUsers = async (): Promise<UserProfile[]> => {
  try {
    // SECURITY: Added limit to prevent exposing all user data at once
    // TODO: Implement proper pagination in UI
    const q = query(
      collection(db, 'users'),
      orderBy('createdAt', 'desc'),
      limit(100) // Limit to 100 users per query
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        uid: data.uid,
        email: data.email,
        role: data.role,
        createdAt: data.createdAt,
        displayName: data.displayName,
        emailVerified: data.emailVerified,
        disabled: data.disabled,
        disabledAt: data.disabledAt,
        disabledBy: data.disabledBy,
        originalRole: data.originalRole,
        createdBy: data.createdBy
      };
    });
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

// Paginated version for large datasets
export const getAllUsersPaginated = async (
  pageSize: number = 50,
  lastDoc?: DocumentSnapshot
): Promise<{ users: UserProfile[], lastDoc: DocumentSnapshot | null }> => {
  try {
    let q = query(
      collection(db, 'users'),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    const users = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        uid: data.uid,
        email: data.email,
        role: data.role,
        createdAt: data.createdAt,
        displayName: data.displayName,
        emailVerified: data.emailVerified,
        disabled: data.disabled,
        disabledAt: data.disabledAt,
        disabledBy: data.disabledBy,
        originalRole: data.originalRole,
        createdBy: data.createdBy
      };
    });

    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    return { users, lastDoc: lastVisible };
  } catch (error) {
    console.error('Error getting paginated users:', error);
    throw error;
  }
};

export const updateUserRole = async (uid: string, role: UserRole): Promise<void> => {
  try {
    const docRef = doc(db, 'users', uid);
    await updateDoc(docRef, { role });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

// CLIENT-SIDE USER MANAGEMENT FUNCTIONS

// STEP 1: Create pending user entry (admin creates placeholder)
export const createPendingUser = async (
  email: string,
  role: UserRole = 'user'
): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be logged in to create users');
  }

  // Check if current user is admin
  const adminProfile = await getUserProfile(currentUser.uid);
  if (adminProfile?.role !== 'admin') {
    throw new Error('Only administrators can create users');
  }

  const adminUid = currentUser.uid;

  try {
    // Check if user already has a pending entry
    const pendingDoc = await getDoc(doc(db, 'pendingUsers', email.toLowerCase()));
    if (pendingDoc.exists()) {
      throw new Error('A pending user with this email already exists');
    }

    // Check if user already exists in users collection by querying for their email
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email.toLowerCase()),
      limit(1)
    );
    const existingUserSnapshot = await getDocs(usersQuery);
    if (!existingUserSnapshot.empty) {
      throw new Error('A user with this email address already exists');
    }

    // Create pendingUser entry WHILE admin is authenticated
    const pendingUserData = {
      email: email.toLowerCase(),
      role: role,
      createdAt: serverTimestamp(),
      createdBy: adminUid
    };

    await setDoc(doc(db, 'pendingUsers', email.toLowerCase()), pendingUserData);

  } catch (error: any) {
    console.error('Error creating pending user:', error);
    throw error;
  }
};

// STEP 2: Activate pending user (creates auth account and sends email)
export const activatePendingUser = async (email: string): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be logged in to activate users');
  }

  // Check if current user is admin
  const adminProfile = await getUserProfile(currentUser.uid);
  if (adminProfile?.role !== 'admin') {
    throw new Error('Only administrators can activate users');
  }

  try {
    // Verify pending user exists
    const pendingDoc = await getDoc(doc(db, 'pendingUsers', email.toLowerCase()));
    if (!pendingDoc.exists()) {
      throw new Error('No pending user found with this email');
    }

    // Create Firebase Auth account with random password
    const randomPassword = `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}Aa1!`;
    await createUserWithEmailAndPassword(auth, email, randomPassword);

    // Send password reset email (auth account exists now!)
    await sendPasswordResetEmail(auth, email);

    // Sign out the new user (admin will need to log back in)
    await signOut(auth);

  } catch (error: any) {
    console.error('Error activating pending user:', error);

    // Handle specific Firebase errors
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('A user with this email address already exists in Authentication');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    }

    throw error;
  }
};

// Get all pending users
export const getPendingUsers = async (): Promise<Array<{email: string, role: UserRole, createdAt: any, createdBy: string}>> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'pendingUsers'));
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        email: data.email,
        role: data.role,
        createdAt: data.createdAt,
        createdBy: data.createdBy
      };
    });
  } catch (error) {
    console.error('Error getting pending users:', error);
    throw error;
  }
};

// Delete pending user (if admin wants to cancel)
export const deletePendingUser = async (email: string): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be logged in to delete pending users');
  }

  // Check if current user is admin
  const adminProfile = await getUserProfile(currentUser.uid);
  if (adminProfile?.role !== 'admin') {
    throw new Error('Only administrators can delete pending users');
  }

  try {
    await deleteDoc(doc(db, 'pendingUsers', email.toLowerCase()));
  } catch (error) {
    console.error('Error deleting pending user:', error);
    throw error;
  }
};

// Client-side user "deletion" (disable + cleanup)
export const deleteUserClientSide = async (userId: string): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be logged in to delete users');
  }

  // Check if current user is admin
  const adminProfile = await getUserProfile(currentUser.uid);
  if (adminProfile?.role !== 'admin') {
    throw new Error('Only administrators can delete users');
  }

  // Prevent admin from deleting themselves
  if (userId === currentUser.uid) {
    throw new Error('You cannot delete your own account');
  }

  try {
    // Get user info for logging
    const userToDelete = await getUserProfile(userId);
    const userEmail = userToDelete?.email || 'unknown';

    // Mark user as disabled in Firestore
    await updateDoc(doc(db, 'users', userId), {
      disabled: true,
      disabledAt: serverTimestamp(),
      disabledBy: currentUser.uid,
      originalRole: userToDelete?.role || 'user'
    });

    // Delete user's calculation history
    const calculationsQuery = query(
      collection(db, 'calculations'),
      where('userId', '==', userId)
    );
    const calculationsSnapshot = await getDocs(calculationsQuery);

    if (!calculationsSnapshot.empty) {
      const batch = writeBatch(db);
      calculationsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      // Security: Calculations deleted (user email not logged)
    }

    // Security: User disabled and data cleaned up (email not logged)

  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Updated getAllUsersActive to exclude disabled users
export const getAllUsersActive = async (): Promise<UserProfile[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    return querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          uid: data.uid,
          email: data.email,
          role: data.role,
          createdAt: data.createdAt,
          displayName: data.displayName,
          emailVerified: data.emailVerified,
          disabled: data.disabled,
          disabledAt: data.disabledAt,
          disabledBy: data.disabledBy,
          originalRole: data.originalRole,
          createdBy: data.createdBy
        };
      })
      .filter(user => !user.disabled);

  } catch (error) {
    console.error('Error getting active users:', error);
    throw error;
  }
};

// Get disabled users (for admin management)
export const getDisabledUsers = async (): Promise<UserProfile[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    return querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          uid: data.uid,
          email: data.email,
          role: data.role,
          createdAt: data.createdAt,
          displayName: data.displayName,
          emailVerified: data.emailVerified,
          disabled: data.disabled,
          disabledAt: data.disabledAt,
          disabledBy: data.disabledBy,
          originalRole: data.originalRole,
          createdBy: data.createdBy
        };
      })
      .filter(user => user.disabled === true);

  } catch (error) {
    console.error('Error getting disabled users:', error);
    throw error;
  }
};

// Optional: Restore a disabled user
export const restoreUser = async (userId: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in');
    }

    const userProfile = await getUserProfile(currentUser.uid);
    if (userProfile?.role !== 'admin') {
      throw new Error('Only administrators can restore users');
    }

    await updateDoc(doc(db, 'users', userId), {
      disabled: false,
      restoredAt: serverTimestamp(),
      restoredBy: currentUser.uid
    });

    console.log('User restored successfully');
  } catch (error) {
    console.error('Error restoring user:', error);
    throw error;
  }
};

// Send password reset email
export const sendPasswordReset = async (email: string): Promise<void> => {
  // SECURITY: Rate limiting to prevent email bombing and account enumeration
  checkRateLimit(`password-reset:${email.toLowerCase()}`, 3, 900000); // 3 attempts per 15 minutes

  try {
    await sendPasswordResetEmail(auth, email);
    resetRateLimit(`password-reset:${email.toLowerCase()}`);
  } catch (error: any) {
    console.error('Error sending password reset email:', error);
    
    // Handle specific Firebase errors
    if (error.code === 'auth/user-not-found') {
      throw new Error('No account found with this email address.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address.');
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('Too many reset attempts. Please try again later.');
    } else if (error.code === 'auth/user-disabled') {
      throw new Error('This account has been disabled. Please contact an administrator.');
    }
    
    throw new Error('Failed to send password reset email. Please try again.');
  }
};

// Verify reset code (optional - for custom reset pages)
export const verifyResetCode = async (code: string): Promise<string> => {
  try {
    const email = await verifyPasswordResetCode(auth, code);
    return email;
  } catch (error: any) {
    console.error('Error verifying reset code:', error);
    
    if (error.code === 'auth/invalid-action-code') {
      throw new Error('Invalid or expired reset code.');
    } else if (error.code === 'auth/expired-action-code') {
      throw new Error('Reset code has expired. Please request a new one.');
    }
    
    throw new Error('Invalid reset code.');
  }
};

// Confirm password reset (optional - for custom reset pages)
export const confirmPasswordResetWithCode = async (code: string, newPassword: string): Promise<void> => {
  try {
    await confirmPasswordReset(auth, code, newPassword);
  } catch (error: any) {
    console.error('Error resetting password:', error);
    
    if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Please use at least 6 characters.');
    } else if (error.code === 'auth/invalid-action-code') {
      throw new Error('Invalid or expired reset code.');
    }
    
    throw new Error('Failed to reset password. Please try again.');
  }
};

// Insurance endoscopy settings functions
export const saveInsuranceEndoscopySettings = async (settings: { [carrier: string]: boolean }): Promise<void> => {
  try {
    const docRef = doc(db, 'appSettings', 'insuranceEndoscopy');
    await setDoc(docRef, {
      settings,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving insurance endoscopy settings:', error);
    throw error;
  }
};

export const getInsuranceEndoscopySettings = async (): Promise<{ [carrier: string]: boolean }> => {
  try {
    const docRef = doc(db, 'appSettings', 'insuranceEndoscopy');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.settings || {};
    } else {
      return {}; // Default: all carriers use enhanced endoscopy (true)
    }
  } catch (error) {
    console.error('Error getting insurance endoscopy settings:', error);
    return {}; // Default: all carriers use enhanced endoscopy (true)
  }
};

// NEW: Insurance bilateral settings functions
export const saveInsuranceBilateralSettings = async (settings: { [carrier: string]: 'option1' | 'option2' }): Promise<void> => {
  try {
    const docRef = doc(db, 'appSettings', 'insuranceBilateral');
    await setDoc(docRef, {
      settings,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving insurance bilateral settings:', error);
    throw error;
  }
};

export const getInsuranceBilateralSettings = async (): Promise<{ [carrier: string]: 'option1' | 'option2' }> => {
  try {
    const docRef = doc(db, 'appSettings', 'insuranceBilateral');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.settings || {};
    } else {
      return {}; // Default: all carriers use option2 (100%, 50% bilateral)
    }
  } catch (error) {
    console.error('Error getting insurance bilateral settings:', error);
    return {}; // Default: all carriers use option2 (100%, 50% bilateral)
  }
};